import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, Save, Loader2, Mail, Phone, Building, Briefcase, Settings } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

interface ProfileData {
  first_name: string;
  last_name: string;
  email: string;
  employee_id: string;
  department?: string;
  position_title?: string;
  school_name?: string;
  phone?: string;
  avatar_url?: string;
  bio?: string;
  goals?: string;
}

export default function UserProfile() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const viewingUserId = searchParams.get('userId');
  const isViewingOtherUser = viewingUserId && viewingUserId !== user?.id;
  const profileId = viewingUserId || user?.id;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<ProfileData>({
    first_name: '',
    last_name: '',
    email: '',
    employee_id: '',
  });

  useEffect(() => {
    if (profileId) {
      loadProfile();
    }
  }, [profileId]);

  const loadProfile = async () => {
    if (!profileId) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', profileId)
        .single();

      if (error) throw error;

      if (data) {
        setProfile({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          employee_id: data.employee_id || '',
          department: data.department || '',
          position_title: data.position_title || '',
          school_name: data.school_name || '',
          phone: data.phone || '',
          avatar_url: data.avatar_url || '',
          bio: data.bio || '',
          goals: data.goals || '',
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };


  const handleSave = async () => {
    if (!user?.id) return;

    setSaving(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: profile.first_name,
          last_name: profile.last_name,
          phone: profile.phone,
          bio: profile.bio,
          goals: profile.goals,
        })
        .eq('id', user.id);

      if (error) throw error;

      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const initials = `${profile.first_name.charAt(0)}${profile.last_name.charAt(0)}`.toUpperCase();

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">
          {isViewingOtherUser ? `${profile.first_name} ${profile.last_name}'s Profile` : 'My Profile'}
        </h1>
        {!isViewingOtherUser && (
          <Link to="/accessibility-settings">
            <Button variant="outline" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Accessibility Settings
            </Button>
          </Link>
        )}
      </div>

      <div className="grid gap-6">
        {/* Avatar Section */}
        <Card>
          <CardContent className="flex items-center gap-6 pt-6">
            <Avatar className="h-24 w-24">
              <AvatarImage src={profile.avatar_url} alt={profile.first_name} />
              <AvatarFallback className="text-2xl">{initials}</AvatarFallback>
            </Avatar>
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {isViewingOtherUser ? (
              // Read-only view for other users
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <p className="text-sm font-medium mt-1">{profile.first_name}</p>
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <p className="text-sm font-medium mt-1">{profile.last_name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${profile.email}`} className="text-sm hover:underline">
                    {profile.email}
                  </a>
                </div>

                {profile.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a href={`tel:${profile.phone}`} className="text-sm hover:underline">
                      {profile.phone}
                    </a>
                  </div>
                )}

                {profile.bio && (
                  <div>
                    <Label>Bio</Label>
                    <p className="text-sm mt-1 text-muted-foreground">{profile.bio}</p>
                  </div>
                )}
              </>
            ) : (
              // Editable view for own profile
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={profile.first_name}
                      onChange={(e) => setProfile(prev => ({ ...prev, first_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={profile.last_name}
                      onChange={(e) => setProfile(prev => ({ ...prev, last_name: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={profile.email}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Email cannot be changed
                  </p>
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number (Optional)</Label>
                  <Input
                    id="phone"
                    value={profile.phone || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <Label htmlFor="bio">Bio (Optional)</Label>
                  <Textarea
                    id="bio"
                    value={profile.bio || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder="Tell us about yourself..."
                    rows={4}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Work Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Work Information
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Employee ID</Label>
                <p className="text-sm font-medium mt-1">{profile.employee_id}</p>
              </div>
              <div>
                <Label>Position</Label>
                <p className="text-sm font-medium mt-1">{profile.position_title || 'Not set'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Department
                </Label>
                <p className="text-sm font-medium mt-1">{profile.department || 'Not set'}</p>
              </div>
              <div>
                <Label>School/Site</Label>
                <p className="text-sm font-medium mt-1">{profile.school_name || 'Not set'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Goals Section */}
        {!isViewingOtherUser && (
          <Card>
            <CardHeader>
              <CardTitle>Professional Goals</CardTitle>
            </CardHeader>
            <CardContent>
              <Label htmlFor="goals">My Goals (Optional)</Label>
              <Textarea
                id="goals"
                value={profile.goals || ''}
                onChange={(e) => setProfile(prev => ({ ...prev, goals: e.target.value }))}
                placeholder="What are your professional development goals for this year?"
                rows={6}
                className="mt-2"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Share your professional development goals to help your mentors and administrators support your growth.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Save Button - Only show for own profile */}
        {!isViewingOtherUser && (
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} size="lg">
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
